import React, { useCallback, useEffect, useMemo, useState } from 'react'
import PropTypes from 'prop-types'
import classnames from 'classnames'

import Select from '../Select'
import MultiLevelSelectedValues from './MultiLevelSelectedValues'

import { useDebouncedSearch } from '../../hooks/useDebouncedSearch'
import useMultiLevelSelectSearch from './useMultiLevelSelectSearch'

import { createNewOptionsState, getOptionValuesToModify, prepareOptionsSelectState } from './helpers'
import { convertFlatToNestedArr } from '../../helpers/arrays'
import { convertFlatDataIdsToMatchValuesType } from './formatters'

const MultiLevelSelect = ({
  className,
  value,
  setFieldValue,
  name,
  // IMPORTANT: when you pass options it should match such field namings ("id" - own id, "parent" - id of parent)
  options: optionsFlatData = [],
  isLoading,
  flatToNestedArrSortFunction,
  CustomMultiFieldItem,
  setFullOptionObject,
  onValuesWithFullObjectSelectCallback,
  shouldSelectParentOnAllChildrenSelected,
  selectedOptionsWrapperClassName,
  initiallyExpandedOptionsIds = [],
  ...selectProps
}) => {
  const [isDataInitiallyFormatted, setIsDataInitiallyFormatted] = useState(false)
  const [options, setOptions] = useState([])
  const [optionsState, setOptionsState] = useState({})

  const isInitialLoading = isLoading || (optionsFlatData.length && !isDataInitiallyFormatted)

  const onMultiValueOptionSelectHandler = useCallback(
    option => {
      const newSelectedValues = getOptionValuesToModify({
        option,
        options,
        optionsState,
        isSelectMode: true,
        shouldSelectParentOnAllChildrenSelected
      })

      // push newly selected values to main value, swap isChecked state to true for options state
      setOptionsState(prevState => createNewOptionsState(prevState, newSelectedValues, true))
      if (setFullOptionObject) {
        const newSelectedValuesWithFullObject = newSelectedValues.map(selected => {
          const fullObject = optionsFlatData.find(opt => opt.id === selected)
          return fullObject
        })

        if (onValuesWithFullObjectSelectCallback) {
          onValuesWithFullObjectSelectCallback(newSelectedValuesWithFullObject)
        } else {
          setFieldValue(name, value.concat(newSelectedValuesWithFullObject))
        }
      } else {
        setFieldValue(name, value.concat(newSelectedValues))
      }
    },
    [
      name,
      options,
      optionsState,
      setFieldValue,
      value,
      optionsFlatData,
      setFullOptionObject,
      onValuesWithFullObjectSelectCallback,
      shouldSelectParentOnAllChildrenSelected
    ]
  )

  const onMultiValueOptionDeselectHandler = useCallback(
    option => {
      const newDeselectedValues = getOptionValuesToModify({ option, options, optionsState })

      const filteredNewValue = value.filter(option => {
        if (setFullOptionObject) {
          return !newDeselectedValues.some(deselected => deselected === option.id)
        } else {
          return !newDeselectedValues.includes(option)
        }
      })

      // remove deselected values from main value, swap isChecked state to false for options state
      setFieldValue(name, filteredNewValue)
      setOptionsState(prevState => createNewOptionsState(prevState, newDeselectedValues))
    },
    [name, options, optionsState, setFieldValue, value, setFullOptionObject]
  )

  const onOptionExpand = useCallback(id => {
    setOptionsState(prevState => ({
      ...prevState,
      [id]: {
        ...prevState[id],
        isExpanded: !prevState[id].isExpanded
      }
    }))
  }, [])

  const { isSearchApplied, searchOptions, handleSearchChange } = useMultiLevelSelectSearch({
    isDataInitiallyFormatted,
    options,
    optionsState,
    setOptionsState
  })

  const { searchText, handleSearchTextChange } = useDebouncedSearch(handleSearchChange)

  const onSearchInputChange = (value, { action }) => {
    if (action === 'input-change') {
      handleSearchTextChange(value)
    }
  }

  // if search is applied and nothing found, show empty options ('No options' message)
  const finalOptions = useMemo(
    () => (isSearchApplied && !searchOptions.length ? [] : options),
    [isSearchApplied, options, searchOptions.length]
  )

  useEffect(() => {
    if (!isDataInitiallyFormatted && optionsFlatData.length) {
      // this helps to make component dynamically work with different types of values (string, number)
      const formattedOptionsFlatData = convertFlatDataIdsToMatchValuesType(optionsFlatData, value)

      setOptions(convertFlatToNestedArr(formattedOptionsFlatData, flatToNestedArrSortFunction))
      setOptionsState(
        prepareOptionsSelectState({
          options: formattedOptionsFlatData,
          selectedValues: value,
          initiallyExpandedOptionsIds,
          setFullOptionObject
        })
      )

      setIsDataInitiallyFormatted(true)
    }
  }, [
    isDataInitiallyFormatted,
    optionsFlatData,
    flatToNestedArrSortFunction,
    value,
    setFullOptionObject,
    initiallyExpandedOptionsIds
  ])

  return (
    <div className={classnames('field', className)}>
      <MultiLevelSelectedValues
        name={name}
        values={value}
        options={options}
        optionsState={optionsState}
        onOptionDeselect={onMultiValueOptionDeselectHandler}
        CustomMultiFieldItem={CustomMultiFieldItem}
        setFullOptionObject={setFullOptionObject}
        selectedOptionsWrapperClassName={selectedOptionsWrapperClassName}
      />
      <Select
        // manually controlled selected value
        selectedValues={value}
        optionsState={optionsState}
        onOptionSelect={onMultiValueOptionSelectHandler}
        onOptionDeselect={onMultiValueOptionDeselectHandler}
        onOptionExpand={onOptionExpand}
        options={finalOptions}
        isSearchApplied={isSearchApplied}
        searchOptions={searchOptions}
        closeMenuOnSelect={false}
        closeMenuOnScroll={false}
        backspaceRemovesValue={false}
        controlShouldRenderValue={false}
        isLoading={isInitialLoading}
        isMultiLevelOptions
        onInputChange={onSearchInputChange}
        inputValue={searchText}
        {...selectProps}
      />
    </div>
  )
}

MultiLevelSelect.propTypes = {
  className: PropTypes.string,
  value: PropTypes.array.isRequired,
  setFieldValue: PropTypes.func.isRequired,
  name: PropTypes.string.isRequired,
  options: PropTypes.array.isRequired,
  isLoading: PropTypes.bool,
  flatToNestedArrSortFunction: PropTypes.func,
  selectedOptionsWrapperClassName: PropTypes.string
}

export default MultiLevelSelect
